import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface DelayedTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  delay?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  width?: string;
  showOnClick?: boolean;
  onClose?: () => void;
}

export const DelayedTooltip: React.FC<DelayedTooltipProps> = ({
  children,
  content,
  delay = 3000,
  position = 'top',
  width = 'w-80',
  showOnClick = true,
  onClose,
}) => {
  const [show, setShow] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setShow(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setShow(false);
  };

  const handleClick = () => {
    if (showOnClick) {
      setShow(!show);
    }
  };

  const handleClose = () => {
    setShow(false);
    onClose?.();
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom': {
        return 'top-full left-0 mt-2';
      }
      case 'left': {
        return 'right-full top-0 mr-2';
      }
      case 'right': {
        return 'left-full top-0 ml-2';
      }
      default: {
        return 'bottom-full left-0 mb-2';
      }
    }
  };

  return (
    <div className="relative inline-block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div onClick={handleClick}>{children}</div>
      {show && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleClose} />
          <motion.div
            initial={{
              opacity: 0,
              y: position === 'top' ? 10 : position === 'bottom' ? -10 : 0,
              x: position === 'left' ? 10 : position === 'right' ? -10 : 0,
            }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            transition={{ duration: 0.2 }}
            className={`absolute ${width} ${getPositionClasses()} bg-alpha-background-secondary rounded-lg shadow-lg z-50 overflow-hidden`}
            style={{
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.14), 0px 1px 2px rgba(0, 0, 0, 0.12)',
              borderRadius: 'var(--alpha-radius-lg)',
              border: '1px solid var(--alpha-border-dark)',
            }}
          >
            {content}
          </motion.div>
        </>
      )}
    </div>
  );
};
